import { useState, useCallback } from 'react';
import FileUpload from './FileUpload';

const DocumentUpload = ({
  onDocumentSelect,
  onDocumentRemove,
  maxSize = 10 * 1024 * 1024, // 10MB default for documents
  maxFiles = 5,
  multiple = true,
  disabled = false,
  className = '',
  uploadedDocuments = [],
  loading = false,
  error = null,
  required = false,
  label = 'Upload Documents',
  documentTypes = ['pdf', 'doc', 'docx', 'txt', 'jpg', 'png'], // Allowed document types
  helperText = ''
}) => {
  const [selectedDocuments, setSelectedDocuments] = useState([]);

  // Define accepted file types based on documentTypes prop
  const getAcceptedTypes = () => {
    const typeMap = {
      pdf: { 'application/pdf': ['.pdf'] },
      doc: { 'application/msword': ['.doc'] },
      docx: { 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'] },
      txt: { 'text/plain': ['.txt'] },
      jpg: { 'image/jpeg': ['.jpg', '.jpeg'] },
      png: { 'image/png': ['.png'] },
      gif: { 'image/gif': ['.gif'] },
      webp: { 'image/webp': ['.webp'] }
    };

    let acceptedTypes = {};
    documentTypes.forEach(type => {
      if (typeMap[type]) {
        acceptedTypes = { ...acceptedTypes, ...typeMap[type] };
      }
    });

    return acceptedTypes;
  };

  const acceptedTypes = getAcceptedTypes();

  const handleDocumentSelect = useCallback((files) => {
    const documentFiles = files.map(file => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    }));

    setSelectedDocuments(prev => [...prev, ...documentFiles]);
    onDocumentSelect(files);
  }, [onDocumentSelect]);

  const handleDocumentRemove = useCallback((index) => {
    setSelectedDocuments(prev => prev.filter((_, i) => i !== index));
    onDocumentRemove(index);
  }, [onDocumentRemove]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getDocumentIcon = (fileType, fileName) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    
    if (fileType === 'application/pdf' || extension === 'pdf') {
      return (
        <svg className="w-8 h-8 text-jet-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
        </svg>
      );
    } else if (fileType.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) {
      return (
        <svg className="w-8 h-8 text-jet-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
        </svg>
      );
    } else if (['doc', 'docx'].includes(extension)) {
      return (
        <svg className="w-8 h-8 text-jet-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
        </svg>
      );
    } else {
      return (
        <svg className="w-8 h-8 text-jet-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
        </svg>
      );
    }
  };

  const getDocumentTypeLabel = (fileType, fileName) => {
    const extension = fileName.split('.').pop()?.toUpperCase();
    
    if (fileType === 'application/pdf') return 'PDF Document';
    if (fileType.startsWith('image/')) return 'Image File';
    if (fileType === 'application/msword') return 'Word Document';
    if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'Word Document';
    if (fileType === 'text/plain') return 'Text File';
    
    return `${extension} File`;
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString();
  };

  return (
    <div className={`document-upload-container ${className}`}>
      <FileUpload
        onFileSelect={handleDocumentSelect}
        onFileRemove={handleDocumentRemove}
        accept={acceptedTypes}
        maxSize={maxSize}
        maxFiles={maxFiles}
        multiple={multiple}
        disabled={disabled}
        uploadedFiles={uploadedDocuments}
        loading={loading}
        error={error}
        required={required}
        label={label}
        showPreview={false} // We'll handle our own preview
        helperText={helperText || `Supported formats: ${documentTypes.join(', ').toUpperCase()} • Max size: ${formatFileSize(maxSize)}`}
      >
        {/* Custom upload area for documents */}
        <div className="flex flex-col items-center">
          <svg className="w-12 h-12 text-jet-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
          </svg>
          <p className="text-sm text-jet-gray-600 mb-1">
            Drag & drop documents here, or click to select
          </p>
          <p className="text-xs text-jet-gray-500">
            {documentTypes.join(', ').toUpperCase()} up to {formatFileSize(maxSize)}
          </p>
        </div>
      </FileUpload>

      {/* Document List */}
      {(selectedDocuments.length > 0 || uploadedDocuments.length > 0) && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-jet-gray-700 mb-3">
            {selectedDocuments.length > 0 ? 'Selected Documents' : 'Uploaded Documents'}
          </h4>
          
          <div className="space-y-2">
            {/* Show selected documents */}
            {selectedDocuments.map((doc, index) => (
              <div key={`selected-${index}`} className="flex items-center justify-between p-4 bg-white border border-jet-gray-200 rounded-lg hover:border-jet-gray-300 transition-colors">
                <div className="flex items-center space-x-3">
                  {getDocumentIcon(doc.type, doc.name)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-jet-gray-900 truncate">
                      {doc.name}
                    </p>
                    <div className="flex items-center space-x-4 text-xs text-jet-gray-500">
                      <span>{getDocumentTypeLabel(doc.type, doc.name)}</span>
                      <span>{formatFileSize(doc.size)}</span>
                      <span>Modified: {formatDate(doc.lastModified)}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  {loading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-jet-blue-500"></div>
                  )}
                  <button
                    type="button"
                    onClick={() => handleDocumentRemove(index)}
                    className="text-jet-red-600 hover:text-jet-red-700 text-sm font-medium"
                    disabled={loading}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}

            {/* Show uploaded documents */}
            {uploadedDocuments.map((doc, index) => (
              <div key={`uploaded-${index}`} className="flex items-center justify-between p-4 bg-white border border-jet-gray-200 rounded-lg hover:border-jet-gray-300 transition-colors">
                <div className="flex items-center space-x-3">
                  {getDocumentIcon(doc.type || doc.mimeType, doc.name || doc.originalName)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-jet-gray-900 truncate">
                      {doc.name || doc.originalName}
                    </p>
                    <div className="flex items-center space-x-4 text-xs text-jet-gray-500">
                      <span>{getDocumentTypeLabel(doc.type || doc.mimeType, doc.name || doc.originalName)}</span>
                      <span>{formatFileSize(doc.size)}</span>
                      {doc.uploadedAt && <span>Uploaded: {formatDate(doc.uploadedAt)}</span>}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  {doc.url && (
                    <button
                      type="button"
                      onClick={() => window.open(doc.url, '_blank')}
                      className="text-jet-blue-600 hover:text-jet-blue-700 text-sm font-medium"
                    >
                      View
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onDocumentRemove(index)}
                    className="text-jet-red-600 hover:text-jet-red-700 text-sm font-medium"
                    disabled={loading}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;